import { useMemo } from 'react';
import { useAppSelector } from 'modules/state-mgmt/store';

/**
 * Custom hook for managing trades data from Redux store
 *
 * @returns {Object} Object containing languageCode, profile constraints, onboarding data, and processed trades
 */
export const useTradesData = () => {
  const {
    languageCode,
    skilledTrades,
    profileFieldConstraintsMap,
    workerId,
    trades,
    isSupervisor,
    isAffiliatedToLaborUnion,
    isSkilled,
    laborUnionNumber,
    assignedCompanies,
    selectedCompanies,
    hasReachedConsentForm,
    isArchivedProject,
  } = useAppSelector(state => ({
    languageCode: state.app?.languageCode,
    skilledTrades: state.cache.skilledTrades || {},
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap || {},
    workerId: state.onboarding.id,
    trades: state.onboarding.trades || [],
    isSupervisor: state.onboarding.isSupervisor ?? null,
    isAffiliatedToLaborUnion: state.onboarding.isAffiliatedToLaborUnion ?? null,
    isSkilled: state.onboarding.isSkilled ?? null,
    laborUnionNumber: state.onboarding.laborUnionNumber || '',
    assignedCompanies: state.onboarding.assignedCompanies || [],
    selectedCompanies: state.onboarding.companies || [],
    hasReachedConsentForm: state.onboarding.temp?.hasReachedConsentForm || false,
    isArchivedProject: state.cache.project.status === 3,
  }));

  const tradesData = useMemo(() => {
    const allTrades = skilledTrades[languageCode] || [];

    const otherTrades = allTrades.find(trade => ['other', 'otro'].includes(trade?.name?.toLowerCase())) || {};

    const masterListTrades = allTrades.filter(trade => trade?.masterList && trade?.id !== otherTrades?.id) || [];

    const nonMasterListTrades = allTrades.filter(trade => !trade?.masterList) || [];

    return {
      masterListTrades,
      nonMasterListTrades,
      otherTrades,
      allTrades,
    };
  }, [skilledTrades, languageCode]);

  return {
    languageCode,
    profileFieldConstraintsMap,
    workerId,
    skilledTrades,
    trades,
    isSupervisor,
    isAffiliatedToLaborUnion,
    isSkilled,
    laborUnionNumber,
    assignedCompanies,
    selectedCompanies,
    hasReachedConsentForm,
    isArchivedProject,
    ...tradesData,
  };
};
